<template>
  <v-dialog :value="dialogProp.show" persistent max-width="700">
    <v-card class="elevation-12">
      <v-toolbar :dark="$vuetify.theme.dark" color="primary">
        <v-toolbar-title>
          {{ title }}
        </v-toolbar-title>
      </v-toolbar>
      <v-row v-if="!!generalError || isLoading" class="my-0 py-0 mx-1">
        <v-col cols="12">
          <v-progress-linear :active="isLoading" color="loader" indeterminate />
        </v-col>
        <v-col class="mx-1">
          <general-error v-if="!!generalError" />
        </v-col>
      </v-row>
      <v-card-text class="pa-3">
        <ValidationObserver ref="clarification">
          <v-row>
            <v-col class="text-left">
              <validatable-textarea
                  v-model="clarification.question"
                  v-test="{ input: 'question' }"
                  :rules="{ required: true,
                            max: 4000 }"
                  :label="$t('Текст запроса')" />
            </v-col>
          </v-row>
          <v-row>
            <v-col class="text-left">
              <validatable-textarea
                  v-model="clarification.answer"
                  v-test="{ input: 'answer' }"
                  :rules="{ required: true,
                            max: 4000 }"
                  :label="$t('Текст ответа')" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="9" class="textcolor--text text-left">
              {{ $t(DOCUMENT_TYPE_ENUM[documentType]) }}
            </v-col>
            <v-col cols="3" class="text-right">
              <typed-file-upload
                  :document-type="documentType"
                  :document-group="documentGroup"
                  :disabled="isLoading"
                  :upload-method="uploadSingleBusinessFile" />
            </v-col>
          </v-row>
          <v-divider class="mb-3" />
          <v-row>
            <text-view
                v-if="file.fileName || (clarificationFromDb.file && clarificationFromDb.file.fileName)"
                multiline
                :display="file.fileName || clarificationFromDb.file.fileName"
                :label="$t('Файл разъяснения')"
                icon="attach_file"
                cols="10"
                :no-data="$t('Не загружен')" />
            <v-col v-if="clarification.fileId" cols="2">
              <btn-icon-tip
                  icon="get_app"
                  :tip="$t('Скачать документ')"
                  @click="getBusinessFile(clarification.fileId)" />
              <btn-icon-tip
                  icon="delete"
                  :tip="$t('Удалить документ')"
                  :disabled="isLoading"
                  @click="deleteFile(clarification.fileId)" />
            </v-col>
          </v-row>
        </ValidationObserver>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
            id="cancelBtn"
            v-test="{ btn: 'cancel' }"
            color="primary"
            @click.stop="cancel">
          {{ $t('Отмена') }}
        </v-btn>
        <v-btn
            id="saveBtn"
            v-test="{ btn: 'save' }"
            color="primary"
            :disabled="isLoading"
            @click.stop="save">
          {{ $t('Сохранить') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import TextView from '@/components/reused/ui/TextView.vue'
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import ValidatableTextarea from '@/components/reused/ui/ValidatableTextarea.vue'
  import TypedFileUpload from '@/components/reused/ui/TypedFileUpload.vue'
  import getFileMixin from '@/utils/mixins/scoped/getFileMixin.js'
  import { DOCUMENT_TYPE_ENUM } from '@/constants/enums'
  import { DOCUMENT_TYPES, BUSINESS_DOCUMENT_GROUPS } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import { LOT_CLARIFICATIONS_MANAGEMENT, BUSINESS_FILES } from '@/constants/store.js'

  export default {
    components: {
      GeneralError,
      TextView,
      BtnIconTip,
      ValidatableTextarea,
      TypedFileUpload
    },
    mixins: [
      getFileMixin('isLoading')
    ],
    props: {
      dialogProp: {
        type: Object,
        default: () => {
          return {
            show: false,
            clarificationId: null,
            lot: null,
            callback: null
          }
        }
      }
    },
    data () {
      return {
        isLoading: false,
        documentType: DOCUMENT_TYPES.CLARIFICATION,
        documentGroup: BUSINESS_DOCUMENT_GROUPS.CLARIFICATION,
        DOCUMENT_TYPE_ENUM,
        clarification: {
          question: null,
          answer: null,
          fileId: null
        }
      }
    },
    computed: {
      ...mapState(LOT_CLARIFICATIONS_MANAGEMENT.NAMESPACE, {
        clarificationFromDb: state => (state[LOT_CLARIFICATIONS_MANAGEMENT.STATE.CURRENT_CLARIFICATION] || {})
      }),
      ...mapState(BUSINESS_FILES.NAMESPACE, {
        file: state => (state[BUSINESS_FILES.STATE.CURRENT_FILE] || {})
      }),
      title () {
        return this.$t(this.dialogProp.clarificationId
          ? 'Редактирование разъяснения по документам'
          : 'Добавление разъяснения по документам')
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        if (!vm.dialogProp.clarificationId ||
          !vm.dialogProp.lot?.lotId) {
          return
        }
        vm.getClarificationInfo()
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(LOT_CLARIFICATIONS_MANAGEMENT.NAMESPACE, {
        getClarificationInfoAction: LOT_CLARIFICATIONS_MANAGEMENT.ACTIONS.GET_CLARIFICATION_INFO,
        createClarificationAction: LOT_CLARIFICATIONS_MANAGEMENT.ACTIONS.CREATE_CLARIFICATION,
        updateClarificationAction: LOT_CLARIFICATIONS_MANAGEMENT.ACTIONS.UPDATE_CLARIFICATION,
        clearClarificationInfoAction: LOT_CLARIFICATIONS_MANAGEMENT.ACTIONS.CLEAR_CLARIFICATION_INFO
      }),
      ...mapActions(BUSINESS_FILES.NAMESPACE, {
        uploadSingleBusinessFileAction: BUSINESS_FILES.ACTIONS.UPLOAD_SINGLE_BUSINESS_FILE,
        deleteBusinessFileAction: BUSINESS_FILES.ACTIONS.DELETE_BUSINESS_FILE,
        clearSingleBusinessFileAction: BUSINESS_FILES.ACTIONS.CLEAR_SINGLE_BUSINESS_FILE
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearClarificationInfoAction(),
            this.clearSingleBusinessFileAction()
          ])
      },
      deleteFile: async function (fileId) {
        try {
          this.isLoading = true
          await this.deleteBusinessFileAction({ fileId })
          await this.clearSingleBusinessFileAction()
          this.clarification.fileId = null
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      uploadSingleBusinessFile: async function (form, documentType, documentGroup) {
        // try catch не нужен (если ошибка, обработаем в месте вызова метода)

        if (this.isAdmin) {
          form.append('creatorId', this.dialogProp.lot.creatorId)
        }

        await this.uploadSingleBusinessFileAction({
          form,
          documentType,
          documentGroup
        })
        this.clarification.fileId = this.file.fileId
      },
      getClarificationInfo: async function () {
        try {
          this.isLoading = true
          await this.getClarificationInfoAction({
            lotId: this.dialogProp.lot.lotId,
            lotType: this.dialogProp.lot.lotType,
            clarificationId: this.dialogProp.clarificationId
          })
          this.mapFields(this.clarificationFromDb)
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      mapFields: function (clarification) {
        this.clarification.question = clarification.question
        this.clarification.answer = clarification.answer
        this.clarification.fileId = clarification.file ? clarification.file.fileId : null
      },
      save: async function () {
        try {
          const result = await this.$refs.clarification.validate()
          if (!result) {
            return
          }

          const model = {
            lotId: this.dialogProp.lot.lotId,
            lotType: this.dialogProp.lot.lotType,
            clarificationId: this.dialogProp.clarificationId,
            clarification: this.clarification
          }
          this.isLoading = true
          if (this.dialogProp.clarificationId) {
            await this.updateClarificationAction(model)
          } else {
            await this.createClarificationAction(model)
          }

          this.isLoading = false
          if (this.dialogProp.callback) {
            this.dialogProp.callback()
          }
          this.cancel()
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      cancel: function () {
        this.$emit('close')
      }
    }
  }
</script>
